// src/components/ui/IconButton.js
import React, { useState } from 'react';

/**
 * Reusable Icon Button Component with unified hover behavior
 *
 * @param {React.ReactNode} children - The icon component to render
 * @param {function} onClick - Click handler
 * @param {string} title - Tooltip text
 * @param {string} size - Size preset: 'sm', 'md', 'lg' (default: 'md')
 * @param {boolean} showBackground - Whether to show background rectangle (default: true)
 * @param {string} hoverColor - Tailwind color class for hover (default: 'text-app-gray-700')
 * @param {boolean} disabled - Whether button is disabled
 * @param {string} className - Additional classes to apply
 * @param {object} props - Additional props to pass to button
 */
const IconButton = React.forwardRef(({
  children,
  onClick,
  title,
  size = 'md',
  showBackground = true,
  hoverColor = 'text-app-gray-600',
  disabled = false,
  className = '',
  ...props
}, ref) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);
  
  // Size mappings: [iconSize, padding]
  const sizeMap = {
    sm: { iconSize: 'w-comp-md h-comp-md', padding: 'p-app-xs' },   // 12px icon, 4px padding
    md: { iconSize: 'w-comp-lg h-comp-lg', padding: 'p-[6px]' },   // 16px icon, 6px padding
    lg: { iconSize: 'w-comp-xl h-comp-xl', padding: 'p-[6px]' },   // 20px icon, 6px padding
  };
  
  const sizeConfig = sizeMap[size] || sizeMap.md;
  
  // Determine icon color based on disabled and hover state
  const getIconColor = () => {
    if (disabled) return 'text-app-gray-300'; // Gray out when disabled
    if (isHovered) return hoverColor;
    return 'text-app-gray-500'; // Default color
  };
  
  // Clone children to add size and color classes
  const iconWithProps = React.cloneElement(children, {
    className: `${sizeConfig.iconSize} ${getIconColor()} transition-colors`,
  });
  
  return (
    <button
      ref={ref}
      onClick={onClick}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsActive(false);
      }}
      onMouseDown={() => !disabled && setIsActive(true)}
      onMouseUp={() => setIsActive(false)}
      disabled={disabled}
      className={`
        ${showBackground ? sizeConfig.padding : ''}
        ${showBackground ? 'rounded-app-sm' : ''}
        ${showBackground && isActive && !disabled ? 'bg-app-gray-400' : ''}
        ${showBackground && isHovered && !isActive && !disabled ? 'bg-app-gray-300' : ''}
        transition-colors
        flex items-center justify-center flex-shrink-0
        ${className}
      `}
      title={title}
      {...props}
    >
      {iconWithProps}
    </button>
  );
});

IconButton.displayName = 'IconButton';

export default IconButton;